/* eslint-disable */
import { createApp, defineAsyncComponent } from 'vue'
import App from './App.vue'
import searchModalStore from "@/store/search.modal.store";
import sideMenuStore from "@/store/sidemenu.store";
import playlistStore from "@/store/playlist.store";
import userStore from "@/store/user.store";
import appStore from "@/store/app.store";
import shareModalStore from "@/store/share.modal.store";
import dialogStore from "@/store/dialog.store";
import * as Sentry from "@sentry/vue";
import Vue3TouchEvents from "vue3-touch-events";

import i18n from './i18n'
import router from './router';
import './index.css'
import './interceptor/fetch.js'
import './service/cache.js'
import 'smoothscroll-polyfill'
import { polyfill } from "mobile-drag-drop";
import { scrollBehaviourDragImageTranslateOverride } from "mobile-drag-drop/scroll-behaviour";
import "mobile-drag-drop/default.css";

polyfill({
    dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride
});

import './registerServiceWorker'

// Catch resource loading errors (e.g. 404 CSS/JS after a new deployment) and auto-reload to fetch new index.html
window.addEventListener('error', (event) => {
  const target = event.target;
  if (target && (target.tagName === 'LINK' || target.tagName === 'SCRIPT')) {
    const src = target.src || target.href || '';
    if (src.includes('/css/') || src.includes('/js/')) {
      console.warn('Failed to load deployment asset:', src);
      const now = Date.now();
      const lastReload = parseInt(sessionStorage.getItem('triad_asset_reload') || '0', 10);
      if (now - lastReload > 8000) {
        sessionStorage.setItem('triad_asset_reload', now.toString());
        const url = new URL(window.location.href);
        url.searchParams.set('u', now.toString());
        console.warn("Auto-reload prevented for debugging:", url.toString());
        // window.location.replace(url.toString());
      }
    }
  }
}, true);

// Clean up cache-busting query parameter from URL immediately
try {
  const url = new URL(window.location.href);
  if (url.searchParams.has('u')) {
    url.searchParams.delete('u');
    window.history.replaceState({}, document.title, url.toString());
  }
} catch (e) {
  console.error('Error cleaning up cache-busting query param:', e);
}

// Remove FOUC guard once CSS/JS bundles load and app initializes
if (typeof document !== 'undefined') {
  const foucGuard = document.getElementById('fouc-guard');
  if (foucGuard) foucGuard.remove();
}

window.cacheInstance = window.localCache('triad')

if (process.env.NODE_ENV === 'production') {
  console.log = () => {}
  console.debug = () => {}
  console.info = () => {}
} else {
  const originalConsoleLog = console.log;
  console.log = (...args) => {
    appStore.dispatch('addEventLog', { message: args.join(' '), type: 'info' })
    originalConsoleLog(...args);
  }
}

window.deferredPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window.deferredPrompt = e;
  window.dispatchEvent(new Event('app-installable'));
});

const listOfComponents = [
  {name: "AppLayout", component: defineAsyncComponent(() => import("@/layouts/AppLayout.vue"))},
  {name: "AppLayoutV2", component: defineAsyncComponent(() => import("@/layouts/AppLayoutV2.vue"))},
  {name: "EmptyLayout", component: defineAsyncComponent(() => import("@/layouts/EmptyLayout.vue"))},

  {name: "Modal", component: defineAsyncComponent(() => import("@/components/common//TriadModal.vue"))},
  {name: "Drawer", component: defineAsyncComponent(() => import("@/components/common/TriadDrawer.vue"))},
  {name: "NavBarMenu", component: defineAsyncComponent(() => import("@/components/common/TriadNavBarMenu.vue"))},
  {name: "SidePanel", component: defineAsyncComponent(() => import("@/components/common/TriadSidePanel.vue"))},
  {name: "Card", component: defineAsyncComponent(() => import("@/components/common/TriadCard.vue"))},
  {name: "Table", component: defineAsyncComponent(() => import("@/components/common/TriadTable.vue"))},
  {name: "Thead", component: defineAsyncComponent(() => import("@/components/common/TriadHead.vue"))},
  {name: "HeadColumn", component: defineAsyncComponent(() => import("@/components/common/TriadHeadColumn.vue"))},
  {name: "TableRow", component: defineAsyncComponent(() => import("@/components/common/TriadTableRow.vue"))},
  {name: "Counter", component: defineAsyncComponent(() => import("@/components/common/TriadCounter.vue"))},
  {name: "TriadPagination", component: defineAsyncComponent(() => import("@/components/common/TriadPagination.vue"))},
  {name: "Divider", component: defineAsyncComponent(() => import("@/components/common/TriadDivider.vue"))},
  {name: "FormView", component: defineAsyncComponent(() => import("@/components/common/TriadFormView.vue"))},
  


  {name: "Logo", component: defineAsyncComponent(() => import("@/components/TriadLogo.vue"))},
  {name: "SideMenu", component: defineAsyncComponent(() => import("@/components/common/SideMenu.vue"))},
  
  {name: "UserAvatarMenu", component: defineAsyncComponent(() => import("@/components/TriadUserAvatarMenu.vue"))},
  {name: "UserAvatar", component: defineAsyncComponent(() => import("@/components/common/UserAvatar.vue"))},
  
  {name: "ModalSearch", component: defineAsyncComponent(() => import("@/components/TriadModalSearch.vue"))},
  {name: "MenuSide", component: defineAsyncComponent(() => import("@/components/TriadMenuSide.vue"))},
  {name: "CardLoading", component: defineAsyncComponent(() => import("@/components/TriadCardLoading.vue"))},
  {name: "Transpose", component: defineAsyncComponent(() => import("@/components/TriadTranspose.vue"))},
  {name: "SongOptions", component: defineAsyncComponent(() => import("@/components/song/TriadSongOptions.vue"))},
  {name: "Loading", component: defineAsyncComponent(() => import("@/components/TriadLoad.vue"))},
  {name: "SongTable", component: defineAsyncComponent(() => import("@/components/TriadSongTable.vue"))},
  {name: "TextNotes", component: defineAsyncComponent(() => import("@/components/TriadTextNotes.vue"))},
  {name: "MainMenuSide", component: defineAsyncComponent(() => import("@/components/TriadMainMenuSide.vue"))},
  {name: "PlaylistMenuSide", component: defineAsyncComponent(() => import("@/components/TriadPlaylistMenuSide.vue"))},
  {name: "PlaylistMediaSidePanel", component: defineAsyncComponent(() => import("@/components/playlist/PlaylistMediaSidePanel.vue"))},
  {name: "SongListSidePanel", component: defineAsyncComponent(() => import("@/components/song/TriadSongListSidePanel.vue"))},
  {name: "ModalPlaylist", component: defineAsyncComponent(() => import("@/components/TriadModalPlaylist.vue"))},
  {name: "AddSongPlaylist", component: defineAsyncComponent(() => import("@/components/TriadAddSongPlaylist.vue"))},
  {name: "FormPlaylist", component: defineAsyncComponent(() => import("@/components/TriadFormPlaylist.vue"))},
  {name: "WebSocket", component: defineAsyncComponent(() => import("@/components/TriadWebSocket.vue"))},
  {name: "PlaylistTable", component: defineAsyncComponent(() => import("@/components/TriadPlaylistTable.vue"))},
  {name: "Presentation", component: defineAsyncComponent(() => import("@/components/presentation/TriadPresentation.vue"))},
  {name: "PresentationSongs", component: defineAsyncComponent(() => import("@/components/presentation/TriadPresentationSongs.vue"))},
  {name: "PresentationConfig", component: defineAsyncComponent(() => import("@/components/presentation/TriadPresentationConfig.vue"))},
  {name: "PresentationPage", component: defineAsyncComponent(() => import("@/components/presentation/TriadPresentationPage.vue"))},
  {name: "ListFiles", component: defineAsyncComponent(() => import("@/components/TriadListFiles.vue"))},
  {name: "ModalShare", component: defineAsyncComponent(() => import("@/components/playlist/TriadModalShare.vue"))},


  {name: "CloseButton", component: defineAsyncComponent(() => import("@/components/TriadCloseButton.vue"))},

  {name: "FileInput", component: defineAsyncComponent(() => import("@/components/form/TriadFileInput.vue"))},
  {name: "Button", component: defineAsyncComponent(() => import("@/components/form/TriadButton.vue"))},
  {name: "TextArea", component: defineAsyncComponent(() => import("@/components/form/TriadTextArea.vue"))},
  {name: "FormContainer", component: defineAsyncComponent(() => import("@/components/form/TriadFormContainer.vue"))},
  {name: "Label", component: defineAsyncComponent(() => import("@/components/form/TriadLabel.vue"))},
  {name: "Input", component: defineAsyncComponent(() => import("@/components/form/TriadInput.vue"))},
  {name: "Range", component: defineAsyncComponent(() => import("@/components/form/TriadRange.vue"))},
  {name: "AudioRange", component: defineAsyncComponent(() => import("@/components/form/TriadAudioRange.vue"))},
  {name: "CreateEditData" , component: defineAsyncComponent(() => import('@/components/form/CreateEditData.vue'))},
  {name: "ListData", component: defineAsyncComponent(() => import("@/components/form/ListData.vue"))},

  {name: "Tooltip", component: defineAsyncComponent(() => import("@/components/common/elements/TriadTooltip.vue"))},
  {name: "Toast", component: defineAsyncComponent(() => import("@/components/common/elements/TriadToast.vue"))},
  {name: "Dialog", component: defineAsyncComponent(() => import("@/components/common/elements/TriadDialog.vue"))},
  {name: "PullToRefresh", component: defineAsyncComponent(() => import("@/components/common/elements/TriadPullToRefresh.vue"))},
  // SONG
  {name: "SongPreview", component: defineAsyncComponent(() => import("@/components/song/TriadSongPreview.vue"))},

  // Media
  {name: "Video", component: defineAsyncComponent(() => import("@/components/TriadVideo.vue"))},
  {name: "Audio", component: defineAsyncComponent(() => import("@/components/TriadAudio.vue"))},
  
  // Schedule Components
  {name: "EventsPopup", component: defineAsyncComponent(() => import("@/components/schedule/EventsPopup.vue"))},
  
  // Artist Components
  {name: "ArtistInfoSidePanel", component: defineAsyncComponent(() => import("@/components/artist/TriadArtistInfoSidePanel.vue"))}
]
// NEW
async function enableMocking() {
    if (process.env.NODE_ENV !== 'development') {
      return
    }   
    const { worker } = await import('./mocks/browser')

    return worker.start()
}

const savedPlaces = new WeakMap()

const replaceElement = (el, binding) => {
  const replacer = document.createComment(' ')
  
  if (el.getAttribute("id")) {
    savedPlaces.set(el, { parentNode: el.parentNode, replacer })
    el.parentNode?.replaceChild(replacer, el)
  } else {
    el.parentNode?.removeChild(el)
  }
}

const showElement = (el) => {
  const ctx = savedPlaces.get(el)
  if (ctx && ctx.replacer) {
      ctx.parentNode?.replaceChild(el, ctx.replacer)
      savedPlaces.delete(el)
  }
}
const executeUpdate = (el, binding) => {
  if (!userStore.state.userInfo) {
    return
  }
  if (userStore.state.userInfo.role==="ADM"){
    showElement(el, binding);
    return
  } else {
  if(userStore.state.userInfo.iam && 
      userStore.state.userInfo.iam.permissions && 
      userStore.state.userInfo.iam.permissions.filter((p) => p.resource.indexOf(binding.arg) > -1).length > 0) {
      showElement(el, binding)
    } 
  }
}
const checkPermission = {
  mounted(el, binding) {
    replaceElement(el, binding)
    executeUpdate(el,binding)
  },
  updated (el, binding){
    executeUpdate(el,binding)
  }
}

const resizeDirective = {
  mounted(el) {
    makeResizable(el)
  }
}

function makeResizable(element) {
  const resizers = element.querySelectorAll('.resizer');
  let isResizing = false;
  let currentResizer = null;
  let startX, startY, startWidth, startHeight;

  resizers.forEach(resizer => {
      resizer.addEventListener('mousedown', initResize);
      resizer.addEventListener('touchstart', initResize, { passive: false });
  });

  function initResize(e) {
      e.preventDefault();
      isResizing = true;
      currentResizer = e.target;
      
      // Handle both mouse and touch events
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
      
      startX = clientX;
      startY = clientY;
      startWidth = parseInt(window.getComputedStyle(element).width, 10);
      startHeight = parseInt(window.getComputedStyle(element).height, 10);

      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);
      document.addEventListener('touchmove', resize, { passive: false });
      document.addEventListener('touchend', stopResize);
      
      element.style.userSelect = 'none';
  }

  function resize(e) {
      if (!isResizing) return;

      // Handle both mouse and touch events
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : startX);
      const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : startY);

      const dx = clientX - startX;
      const dy = clientY - startY;

      if (currentResizer.classList.contains('resizer-right') || 
          currentResizer.classList.contains('resizer-corner')) {
          const newWidth = startWidth + dx;
          if (newWidth > 100) {
              element.style.width = newWidth + 'px';
          }
      }

      if (currentResizer.classList.contains('resizer-bottom') || 
          currentResizer.classList.contains('resizer-corner')) {
          const newHeight = startHeight + dy;
          if (newHeight > 100) {
              element.style.height = newHeight + 'px';
          }
      }

      updateSizeDisplay();
  }

  function stopResize() {
      isResizing = false;
      currentResizer = null;
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
      document.removeEventListener('touchmove', resize);
      document.removeEventListener('touchend', stopResize);
      element.style.userSelect = '';

      element.dispatchEvent(new CustomEvent('stopResize', {detail: getDimensions()}));
  }

  function updateSizeDisplay() {
      element.dispatchEvent(new CustomEvent('resize', {detail: getDimensions()}));
  }

  function getDimensions() {
    return {
      width: parseInt(window.getComputedStyle(element).width, 10),
      height: parseInt(window.getComputedStyle(element).height, 10)
    }
  }
}

enableMocking().then( async () => {
    // Detect new deploy / version change and clear stale caches automatically
    try {
        const vRes = await fetch(`/version.json?t=${Date.now()}`);
        if (vRes.ok) {
            const vData = await vRes.json();
            const currentBuildId = `${vData.version}-${vData.commit}-${vData.buildTime || ''}`;
            const previousBuildId = localStorage.getItem('triad_deployed_build_id');
            if (previousBuildId && previousBuildId !== currentBuildId) {
                console.log(`[Deploy Update] Refreshing asset caches for build: ${previousBuildId} -> ${currentBuildId}`);
                if (typeof caches !== 'undefined') {
                    try {
                        const cacheKeys = await caches.keys();
                        await Promise.all(cacheKeys.map(k => caches.delete(k)));
                    } catch (cErr) {}
                }
                localStorage.removeItem('triad_offline_menus');
                localStorage.removeItem('triad_offline_teams');
                localStorage.removeItem('triad_offline_env');
            }
            localStorage.setItem('triad_deployed_build_id', currentBuildId);
        }
    } catch (vErr) {
        console.warn('Could not verify version.json on startup:', vErr);
    }

    let env = {};
    try {
        const response = await fetch('/env.json', {headers: {
            'Content-Type': 'application/json'
        }});
        if (response.ok) {
            env = await response.json();
            localStorage.setItem('triad_offline_env', JSON.stringify(env));
        }
    } catch (e) {
        console.warn("Offline fallback for /env.json", e);
        const cachedEnv = localStorage.getItem('triad_offline_env');
        if (cachedEnv) {
            try {
                env = JSON.parse(cachedEnv);
            } catch (err) {
                console.error("Error parsing cached env", err);
            }
        }
    }

    // Make it available globally
    window.__RUNTIME_CONFIG__ = env || {};

    // Initialize Capacitor listeners if running in native app
    let withTimeoutFn = null;
    try {
        const capUtils = await import('./utils/capacitor');
        const { initAppListeners, initStatusBar } = capUtils;
        withTimeoutFn = capUtils.withTimeout || null;
        if (typeof initStatusBar === 'function') {
            initStatusBar().catch(() => {});
        }
        const { appEvents } = await import('./store/app.events');
        await initAppListeners((incomingUrl) => {
            // Normaliza: conforme versão do plugin/build, pode chegar string
            // ou objeto { url }. Sem isso, .match() quebra e o sid nunca é salvo.
            let rawUrl = incomingUrl;
            if (rawUrl && typeof rawUrl === 'object') {
                rawUrl = rawUrl.url || null;
            }
            console.log('[auth] return raw=' + String(rawUrl).slice(0, 90));
            let sid = null;
            if (typeof rawUrl === 'string' && rawUrl) {
                try {
                    const url = new URL(rawUrl);
                    sid = url.searchParams.get('session_id');
                } catch (err) {}

                if (!sid) {
                    const match = rawUrl.match(/[?&]session_id=([^&#]+)/i);
                    if (match) {
                        sid = decodeURIComponent(match[1]);
                    }
                }

                if (sid) {
                    console.log('[auth] session_id stored, len=' + sid.length);
                    localStorage.setItem('triad_session_id', sid);
                    try {
                        import('@capacitor/browser').then(({ Browser }) => Browser.close().catch(() => {})).catch(() => {});
                    } catch (_) {}
                } else {
                    console.log('[auth] no session_id in return URL');
                }
            }
            
            appEvents.emit('app:auth-resumed', sid);

            // Resume sem deep link (appStateChange) durante login em progresso:
            // não dispara info() aqui — o LoginView aguarda o deep link chegar.
            // Sem sessão salva e sem sid novo: usuário está deslogado, evita fetch
            // prematuro que fatalmente daria 401 e cancelaria o fluxo de auth.
            const authInProgress = sessionStorage.getItem('triad_auth_in_progress') === 'true';
            const hasSession = !!localStorage.getItem('triad_session_id');
            if (!sid && (authInProgress || !hasSession)) {
                if (authInProgress) {
                    console.log('App resume during auth in progress, deferring user info load.');
                }
                return;
            }
            
            userStore.dispatch('info', true).then(() => {
                // Só navega após LOGIN via deep link (com sid). No resume comum
                // (ex.: voltar da galeria no meio do perfil), apenas atualiza
                // em silêncio — navegar aqui jogava o usuário no dashboard e
                // descartava a foto/cropper.
                if (sid && userStore.state.userInfo && (userStore.state.userInfo.id || userStore.state.userInfo.email)) {
                    router.replace('/');
                }
            }).catch((err) => {
                console.warn('Post-auth user info dispatch failed:', err);
            });
        });
    } catch (e) {
        console.warn('Capacitor native listener init ignored on web:', e);
    }

    // Pre-fetch user session if available before mounting (com timeout:
    // o boot nunca pode travar se a rede pendurar com o app fechado há dias).
    try {
        const prefetch = typeof withTimeoutFn === 'function'
            ? withTimeoutFn(userStore.dispatch('info'), 8000)
            : userStore.dispatch('info');
        await prefetch;
    } catch (e) {
        console.warn('Initial user info load deferred:', e);
    }

    let app = createApp(App)
    //TODO: Why don't work?
    //app.component("UserDropDown", defineAsyncComponent(() => import("@/components/TriadUserDropDown.vue")))

    Sentry.init({
      app,
      dsn: "https://8957902ce68e834488e11161cc25f3d4@o4509532950953984.ingest.us.sentry.io/4509981608181760",
      // This enables automatic instrumentation (highly recommended),
      // but is not necessary for purely manual usage
      // If you only want to use custom instrumentation:
      // * Remove the BrowserTracing integration
      // * add Sentry.addTracingExtensions() above your Sentry.init() call
      // integrations: [Sentry.browserTracingIntegration()],
    
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: 1.0,
      sendDefaultPii: true
    });
    
    listOfComponents.forEach(item => app.component(item.name, item.component) );

    app.use(playlistStore)
    app.use(searchModalStore)
    app.use(sideMenuStore)
    app.use(shareModalStore)
    app.use(dialogStore)
    app.use(router)
    app.use(Vue3TouchEvents)
    app.use(i18n)
    app.directive('iam', checkPermission)
    app.directive('resize', resizeDirective)
    app.mount('#app')

    if (typeof window !== 'undefined' && typeof window.__dismissSplash === 'function') {
      window.__dismissSplash();
    }
})

